import { useState, useEffect } from 'react';
import {
  Button,
  Checkbox,
  Form, message,
  Modal,
  Spin,
} from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRequest } from 'ahooks';
import qs from 'query-string';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { isEmpty, map } from 'lodash';
import { nanoid } from 'nanoid';
import { getDictType } from '@/services/common';
import './index.less';
import SubjectBasicInfo from './components/SubjectBasicInfo';
import useKeepAlive from '@/hooks/useKeepAlive';
import { XMDD_ARG_PRO } from '@/utils/mappingDict';
import AuthorizationInfo from '@/components/FormInfo/AuthorizationInfo';
import {
  agrProductAddApply,
  getAgrProductDetail, getAgrProductRelease,
  updateAgrProduct,
} from '@/services/projectScheduling';
import { getFileList } from '@/utils/fileDataUtil';
import { addDecimalNum, moneyDelete } from '@/utils/utils';

const formItemLayout = {
  labelCol: {
    sm: { span: 8 },
  },
  wrapperCol: {
    sm: { span: 24 },
  },
};
const curYear = dayjs().year();
export default function Application() {
  const [form] = Form.useForm();
  const formValues = Form.useWatch([], form);
  const Navigate = useNavigate();
  const { search } = useLocation();
  const { id } = qs.parse(search);
  const user = useSelector((state) => state.userInfo);
  const [initialValues] = useState({
    applicantName: user.displayName,
    applicantNo: user.displayCode,
    initiationYear: dayjs(),
  });

  const [authInfoModal, setAuthInfoModal] = useState(false); // 授权书内容
  const [errorRowKeys, setErrorRowKeys] = useState([]); // 提交时有错误的表格行

  const generateFileInfo = (fileList) => fileList.map((file) => ({
    fileName: file.name || file.fileName,
    fileUrl: file?.response?.data || file.fileUrl || file.url,
  }));

  const { data: dict = {} } = useRequest(getDictType, { defaultParams: [XMDD_ARG_PRO] });

  // 农产品发布-数据回显
  const { run: agrReleaseRun, data: agrReleaseData } = useRequest(getAgrProductRelease, {
    manual: true,
    onSuccess(res) {
      form.resetFields();
      if (res) {
        const {
          provinceId, cityId, areaId, projectProvinceId, projectCityId, projectAreaId, initiationYear, projectName, fundUtilizationVos,
        } = res;
        const flag = !isEmpty(fundUtilizationVos);
        const newFundUtilizationVos = fundUtilizationVos?.map((item) => ({
          ...item,
          rowKey: nanoid(),
          financialAidAmount: moneyDelete(item.financialAidAmount),
          investment: moneyDelete(item.investment),
          investmentCompleted: moneyDelete(item.investmentCompleted),
        }));
        form.setFieldsValue({
          applicantName: user.displayName,
          applicantNo: user.displayCode,
          initiationYear: initiationYear ? dayjs(String(initiationYear), 'YYYY') : dayjs(),
          projectName,
          area: provinceId && [provinceId, cityId, areaId],
          projectArea: projectProvinceId && [projectProvinceId, projectCityId, projectAreaId],
          fundUtilizationVos: flag && newFundUtilizationVos,
          totalInvestment: flag ? addDecimalNum(map(fundUtilizationVos, (item) => item.investment)) : 0, // 项目计划总投资
          totalInvestmentCompleted: flag ? addDecimalNum(map(fundUtilizationVos, (item) => item.investmentCompleted)) : 0, // 已完成总投资
          totalFinancialAidAmount: flag ? addDecimalNum(map(fundUtilizationVos, (item) => item.financialAidAmount)) : 0, // 申报财政补助资金总额
        });
      }
    },
  });

  // 数据回显
  const { run: getBasicInfoRun, data } = useRequest(getAgrProductDetail, {
    manual: true,
    onSuccess(res) {
      if (res) {
        const {
          provinceId, cityId, areaId, projectProvinceId, projectCityId, projectAreaId, initiationYear, fileVos, fundUtilizationVos,
        } = res;
        // 生成 specialAgriculturalFundsEquips0, 1, ...
        const temp = {};
        const newFundUtilizationVos = fundUtilizationVos.map((item) => {
          const rowKey = nanoid();
          temp[`specialAgriculturalFundsEquips${rowKey}`] = (item.specialAgriculturalFundsEquips || []).map((equip) => ({
            finishFlag: equip.finishFlag === 1, // 如果 finishFlag 是 1/2，可以转成 true/false
            fileList1: (equip.fileList1 || []).map((f) => ({
              uid: f.id,
              name: f.fileName,
              status: 'done',
              url: f.fileUrl,
              response: { data: f.fileUrl },
            })),
            fileList2: (equip.fileList2 || []).map((f) => ({
              uid: f.id,
              name: f.fileName,
              status: 'done',
              url: f.fileUrl,
              response: { data: f.fileUrl },
            })),
          }));
          return {
            ...item,
            rowKey,
          };
        });
        form.setFieldsValue({
          ...res,
          area: [provinceId, cityId, areaId],
          projectArea: [projectProvinceId, projectCityId, projectAreaId],
          initiationYear: dayjs(String(initiationYear), 'YYYY'),
          fileVos: getFileList(fileVos),
          fundUtilizationVos: newFundUtilizationVos,
          ...temp,
        });
      }
    },
  });

  const { loading, run: add } = useRequest(agrProductAddApply, {
    manual: true,
    onSuccess() {
      message.success('提交成功');
      Navigate('/projectScheduling/myProject');
    },
  });

  const { loading: updateLoading, run: updateRun } = useRequest(updateAgrProduct, {
    manual: true,
    onSuccess() {
      message.success('更新成功');
      Navigate('/projectScheduling/myProject');
    },
  });

  useEffect(() => {
    if (id) {
      getBasicInfoRun(id);
    } else {
      agrReleaseRun();
    }
  }, [id]);

  // 保证token不过期
  useKeepAlive();

  function onFinish() {
    form.validateFields().then((values) => {
      const { fundUtilizationVos, ...rest } = values;
      const mergedFundUtilizationVos = fundUtilizationVos.map((item) => {
        // 取出对应 index 的展开表数据
        const equipExtraList = rest[`specialAgriculturalFundsEquips${item.rowKey}`] || [];
        const originEquipList = item.specialAgriculturalFundsEquips || [];
        const mergedEquipList = originEquipList.map((equip, equipIndex) => {
          const { fileList1, fileList2, finishFlag } = equipExtraList[equipIndex] || {};
          return {
            ...equip,
            finishFlag: finishFlag ? 1 : 2,
            fileList1: fileList1 && generateFileInfo(fileList1),
            fileList2: fileList2 && generateFileInfo(fileList2),
          };
        });
        return {
          ...item,
          specialAgriculturalFundsEquips: mergedEquipList,
        };
      });

      const submitData = {
        ...values,
        provinceId: values.area[0],
        cityId: values.area[1],
        areaId: values.area[2],
        area: undefined,
        projectProvinceId: values.projectArea[0],
        projectCityId: values.projectArea[1],
        projectAreaId: values.projectArea[2],
        projectArea: undefined,
        id,
        initiationYear: values.initiationYear.year().toString(),
        fileVos: generateFileInfo(values.fileVos),
        specialAgriculturalApplicationId: agrReleaseData?.specialAgriculturalApplicationId,
        fundUtilizationVos: mergedFundUtilizationVos,
      };

      // 清理游离字段（非常重要）
      Object.keys(rest).forEach((key) => {
        if (key.startsWith('specialAgriculturalFundsEquips')) {
          delete submitData[key];
        }
      });

      if (id) {
        updateRun(submitData);
      } else {
        add(submitData);
      }
    }).catch((err) => {
      const errorFields = err.errorFields || [];
      const errRowKeys = [];

      errorFields.forEach(({ name }) => {
        const flatName = name.flat(Infinity);

        const key = flatName.find(
          (n) => typeof n === 'string'
            && n.startsWith('specialAgriculturalFundsEquips'),
        );

        if (key) {
          errRowKeys.push(
            key.replace('specialAgriculturalFundsEquips', ''),
          );
        }
      });
      setErrorRowKeys(errRowKeys);
      setTimeout(() => {
        const errorList = document.querySelectorAll('.ant-form-item-has-error');
        errorList[0]?.scrollIntoView({
          block: 'center',
          behavior: 'smooth',
        });
      }, 500);
    });
  }

  function checkAuthorize(_, value) {
    if (!value) {
      return Promise.reject(new Error('勾选同意并接受'));
    }
    return Promise.resolve();
  }

  const hasUploadingFile = (values) => {
    if (!values) return false;

    if (Array.isArray(values)) {
      return values.some((item) => hasUploadingFile(item));
    }

    if (typeof values === 'object') {
      // Upload file 对象
      if (values.status === 'uploading') return true;

      return Object.values(values).some((v) => hasUploadingFile(v));
    }

    return false;
  };

  function calcEquipCompletedAmount(equip) {
    return addDecimalNum([
      equip.provincialSubsidyFunds,
      equip.cityFunding,
      equip.selfFunding,
      equip.otherFunding,
    ]);
  }

  const handleValuesChange = (changedValues, allValues) => {
    const { fundUtilizationVos } = allValues;
    // 找出 specialAgriculturalFundsEquips*
    const changedKey = Object.keys(changedValues).find((k) => k.startsWith('specialAgriculturalFundsEquips'));
    if (!changedKey) return;

    // 从 key 中取 rowKey
    const rowKey = changedKey.replace('specialAgriculturalFundsEquips', '');
    // 找到父表行 index
    const fundIndex = fundUtilizationVos.findIndex(
      (item) => item.rowKey === rowKey,
    );
    if (fundIndex === -1) return;

    const equipFormList = allValues[changedKey] || [];
    const originEquipList = fundUtilizationVos[fundIndex].specialAgriculturalFundsEquips || [];

    let completedAmount = 0;

    equipFormList.forEach((equipForm, equipIndex) => {
      if (!equipForm?.finishFlag) return;

      const originEquip = originEquipList[equipIndex];
      if (!originEquip) return;

      completedAmount = addDecimalNum([
        completedAmount,
        calcEquipCompletedAmount(originEquip),
      ]);
    });

    const newData = fundUtilizationVos.map((item, index) => (index === fundIndex
      ? {
        ...item,
        investmentCompleted: completedAmount,
      }
      : item));

    // 回写父表 investmentCompleted
    form.setFieldsValue({
      fundUtilizationVos: newData,
      totalInvestmentCompleted: addDecimalNum(map(newData, (item) => item.investmentCompleted)),
    });
  };
  const uploading = hasUploadingFile(formValues);

  return (
    <div className="major-financing-application">
      <div className="content-width">
        <div className="margin-top20">
          <Spin spinning={false}>
            <Form
              {...formItemLayout}
              form={form}
              name="form"
              className="form"
              onFinish={onFinish}
              initialValues={initialValues}
              onValuesChange={handleValuesChange}
            >
              <SubjectBasicInfo
                formRef={form}
                data={id ? data : agrReleaseData}
                dict={{
                  ...dict, curYear,
                }}
                errorRowKeys={errorRowKeys}
              />
              <div className="authorize">
                <Form.Item
                  required
                  name="authorize"
                  wrapperCol={{ span: 24 }}
                  valuePropName="checked"
                  rules={[
                    { validator: checkAuthorize },
                  ]}
                >
                  <Checkbox
                    onChange={(e) => {
                      form.setFieldsValue({ authorize: false });
                      if (e.target.checked) {
                        setAuthInfoModal(true);
                      }
                    }}
                  >
                    您需要同意并接受
                    <Button type="link" className="authorize-name" onClick={() => setAuthInfoModal(true)}>《直通车平台业务授权协议》</Button>
                  </Checkbox>
                </Form.Item>
              </div>
              <div className="submit-btn-box">
                <Button type="primary" onClick={onFinish} loading={loading || updateLoading}>
                  提交
                </Button>
              </div>
            </Form>
          </Spin>
          <Modal
            title="授权协议查看"
            open={authInfoModal}
            width={900}
            footer={
                   [
                     <Button key="skip" onClick={() => setAuthInfoModal(false)}>
                       取消
                     </Button>,
                     <Button
                       type="primary"
                       key="confirm"
                       onClick={() => {
                         setAuthInfoModal(false);
                         form.setFieldsValue({ authorize: true });
                       }}
                       disabled={uploading}
                     >
                       确认
                     </Button>,
                   ]
                 }
            onCancel={() => setAuthInfoModal(false)}
          >
            <div dangerouslySetInnerHTML={{ __html: AuthorizationInfo }} style={{ maxHeight: 600, overflowY: 'scroll' }} />
          </Modal>
        </div>
      </div>
    </div>
  );
}
